
import React, { useState } from 'react';
import { Coin } from '../types.ts';
import { AreaChart, Area, ResponsiveContainer, YAxis } from 'recharts';

interface MarketsProps {
  coins: Coin[];
  onCoinClick: (coin: Coin) => void;
}

const Markets: React.FC<MarketsProps> = ({ coins, onCoinClick }) => {
  const [activeTab, setActiveTab] = useState('Prices');
  const tabs = ['Prices', 'Watchlist', 'Price Alert'];

  return (
    <div className="px-5 pt-12 pb-6 space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-white font-bold text-2xl">Market</h2>
        <button className="p-2 bg-zinc-900 rounded-full">
          <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </button>
      </div>

      <div className="flex border-b border-zinc-800">
        {tabs.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`flex-1 pb-3 text-sm font-medium transition-colors relative ${
              activeTab === tab ? 'text-blue-500' : 'text-gray-500'
            }`}
          >
            {tab}
            {activeTab === tab && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-500"></div>}
          </button>
        ))}
      </div>

      <div className="space-y-4">
        {coins.map((coin) => (
          <div 
            key={coin.id} 
            onClick={() => onCoinClick(coin)}
            className="flex items-center justify-between p-2 rounded-xl cursor-pointer hover:bg-zinc-900/50"
          >
            <div className="flex items-center space-x-3 w-1/3">
              <img src={coin.image} className="w-10 h-10 rounded-full" alt={coin.name} />
              <div>
                <h5 className="text-white font-bold text-sm">{coin.name}</h5>
                <p className="text-gray-500 text-[10px]">{coin.symbol.toUpperCase()}</p>
              </div>
            </div>

            <div className="h-10 w-24">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={coin.sparkline_in_7d?.price.map((val, i) => ({ val, i }))}>
                  <defs>
                    <linearGradient id={`color-${coin.id}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={coin.price_change_percentage_24h >= 0 ? "#10B981" : "#EF4444"} stopOpacity={0.3}/>
                      <stop offset="95%" stopColor={coin.price_change_percentage_24h >= 0 ? "#10B981" : "#EF4444"} stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <Area 
                    type="monotone" 
                    dataKey="val" 
                    stroke={coin.price_change_percentage_24h >= 0 ? "#10B981" : "#EF4444"} 
                    fillOpacity={1} 
                    fill={`url(#color-${coin.id})`}
                    strokeWidth={1.5} 
                  />
                  <YAxis hide domain={['auto', 'auto']} />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            <div className="text-right w-1/4">
              <p className="text-white font-bold text-sm">${coin.current_price.toLocaleString()}</p>
              <p className={`text-[10px] font-bold ${coin.price_change_percentage_24h >= 0 ? 'text-emerald-500' : 'text-rose-500'}`}>
                {coin.price_change_percentage_24h >= 0 ? '↑' : '↓'} {Math.abs(coin.price_change_percentage_24h).toFixed(2)}%
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Markets;
